import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, GraduationCap, HeartPulse, ShoppingBag, Sparkles } from "lucide-react";
import CookieConsent from "@/components/CookieConsent";
import WhatsAppButton from "@/components/WhatsAppButton";
import GoogleReviewsButton from "@/components/GoogleReviewsButton";
import AccessibilityButton from "@/components/AccessibilityButton";
import { useSiteSettings } from "@/hooks/useSiteData";
import logo from "@/assets/logo-painur-small.avif";
import { seoHreflangTags } from "@/components/SeoHreflang";
import { seoSocialTags } from "@/components/SeoSocial";

const tiles = [
  { title: "חנות", subtitle: "מוצרים וטיפולים משלימים", description: "\n", to: "https://www.painurex.com", icon: ShoppingBag, kind: "external" as const, cta: "כניסה לחנות", imageKey: "landing_shop_bg", theme: "border-primary/25" },
  { title: "מרפאה", subtitle: "האתר הראשי של בית הרופאים באקה", description: "\n", to: "/clinic", icon: HeartPulse, kind: "internal" as const, cta: "כניסה למרפאה", imageKey: "landing_clinic_bg", theme: "border-border" },
  { title: "מכללה", subtitle: "לימודים, הכשרות וסדנאות", description: "\n", to: "https://college.painur.com", icon: GraduationCap, kind: "external" as const, cta: "כניסה למכללה", imageKey: "landing_college_bg", theme: "border-secondary/30" },
];

function TileLink({ title, subtitle, description, to, kind, cta, icon: Icon, theme, image }: (typeof tiles)[number] & { image?: string }) {
  const content = (
    <div className={`group relative isolate min-h-[240px] overflow-hidden rounded-md border p-6 sm:min-h-[360px] sm:p-8 lg:min-h-[520px] ${theme}`}>
      {image ? <img src={image} alt="" aria-hidden="true" loading="lazy" width={1024} height={1024} className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105" /> : null}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,hsl(var(--foreground)/0.54),hsl(var(--foreground)/0.5)_16%,hsl(var(--foreground)/0.68)_42%,hsl(var(--foreground)/0.84)_72%,hsl(var(--foreground)/0.92))]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,hsl(var(--foreground)/0.18),transparent_22%),linear-gradient(135deg,hsl(var(--foreground)/0.28),transparent_42%,hsl(var(--foreground)/0.18))]" />
      <div className="absolute -left-8 top-8 h-28 w-28 rounded-full border border-foreground/10 bg-background/25 blur-2xl animate-float" />
      <div className="absolute bottom-10 right-6 h-24 w-24 rounded-full border border-foreground/10 bg-secondary/10 blur-2xl animate-float" style={{ animationDelay: "1.2s" }} />

      <div className="relative z-10 flex h-full flex-col items-center text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-background/40 bg-background/70 text-primary shadow-[0_18px_45px_hsl(var(--primary)/0.18)] backdrop-blur-md animate-float">
          <Icon className="h-8 w-8" />
        </div>
        <div className="mt-5 flex w-full flex-1 items-center justify-center">
          <div className="flex min-h-[212px] w-full max-w-[22rem] flex-col justify-center rounded-md border border-background/25 bg-[hsl(var(--foreground)/0.28)] px-5 py-6 shadow-[0_18px_45px_hsl(var(--foreground)/0.26)] backdrop-blur-lg sm:min-h-[228px] sm:px-6">
            <div className="space-y-4">
              <div className="text-gradient text-sm font-medium">{subtitle}</div>
              <h2 className="text-gradient text-4xl font-black leading-none sm:text-5xl lg:text-6xl">{title}</h2>
              <p className="text-gradient mx-auto max-w-sm text-sm leading-7 sm:text-base whitespace-pre-line">{description}</p>
            </div>
          </div>
        </div>
        <div className="mt-7 inline-flex min-h-12 w-full max-w-[22rem] items-center justify-center gap-2 rounded-full border border-background/35 bg-background/75 px-5 py-3 text-sm font-bold text-primary shadow-[0_16px_40px_hsl(var(--foreground)/0.18)] backdrop-blur-md transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-[1.02]">
          {cta}
          <ArrowLeft className="h-4 w-4" />
        </div>
        <div className="absolute left-5 top-5 rounded-full border border-background/20 bg-background/20 p-2 backdrop-blur-sm"><Sparkles className="h-4 w-4 text-background/80 transition-transform duration-500 group-hover:rotate-12" /></div>
        <div className="absolute bottom-5 right-5 rounded-full border border-background/20 bg-background/20 p-2 backdrop-blur-sm text-background/80"><Icon className="h-5 w-5" /></div>
      </div>
    </div>
  );

  return kind === "external"
    ? <a href={to} target="_blank" rel="noreferrer" aria-label={`מעבר ל${title}`} className="block h-full">{content}</a>
    : <Link to={to} aria-label={`מעבר ל${title}`} className="block h-full">{content}</Link>;
}

export default function SplitterLanding() {
  const clinic = useSiteSettings() as Record<string, string | undefined>;
  const tilesWithImages = tiles.map((tile) => ({ ...tile, image: clinic[tile.imageKey] }));

  return (
    <>
      <Helmet>
        <title>בית הרופאים באקה | כניסה למרפאה, לחנות ולמכללה</title>
        <meta name="description" content="דף ניווט ראשי של בית הרופאים באקה עם מעבר מהיר למרפאה, לחנות ולמכללה." />
        <link rel="canonical" href="https://painur.com/" />
        {seoHreflangTags({ canonical: "https://painur.com/" })}
        {seoSocialTags({
          title: "בית הרופאים באקה | כניסה למרפאה, לחנות ולמכללה",
          description: "דף ניווט ראשי של בית הרופאים באקה עם מעבר מהיר למרפאה, לחנות ולמכללה.",
          canonical: "https://painur.com/",
        })}
        <meta name="robots" content="index,follow" />
      </Helmet>

      <main className="min-h-screen overflow-hidden bg-[linear-gradient(180deg,hsl(var(--muted)),hsl(var(--background))_18%,hsl(var(--background)))]">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.10),transparent_30%),radial-gradient(circle_at_bottom,hsl(var(--secondary)/0.14),transparent_28%)]" />
        <div className="relative mx-auto flex min-h-screen w-full max-w-[1680px] flex-col px-4 py-4 sm:px-6 lg:px-10">
          <header className="sticky top-0 z-20 mb-6">
            <div className="rounded-full border border-border/70 bg-background/80 px-4 py-3 shadow-[0_18px_40px_hsl(var(--primary)/0.10)] backdrop-blur-xl">
              <div className="flex items-center justify-center text-center">
                <img src={logo} alt="בית הרופאים באקה - מרפאת כאב ושיקום" width={112} height={112} className="h-16 w-auto sm:h-20" />
              </div>
            </div>
          </header>

          <h1 className="sr-only">בית הרופאים באקה — מרפאת כאב, פיזיותרפיה ושיקום | כניסה למרפאה, חנות ומכללה</h1>


          <section className="grid auto-rows-fr flex-1 gap-4 md:grid-cols-3 md:gap-5" aria-label="בחירת מסלול כניסה">
            {tilesWithImages.map((tile) => <TileLink key={tile.title} {...tile} />)}
          </section>
        </div>
        <CookieConsent />
        <WhatsAppButton />
        <GoogleReviewsButton />
        <AccessibilityButton />
      </main>
    </>
  );
}